import { useMemo } from 'react';

import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core/hooks';

import {
  DelinquentClaimsStatisticTileConfig,
  DelinquentClaimsStatisticTileType,
  UseDelinquentClaimsStatisticLogicParams,
} from '../DelinquentClaimsStatistic.types';

export const useDelinquentClaimsStatisticLogic = ({
  data,
  isLoading,
}: UseDelinquentClaimsStatisticLogicParams) => {
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();

  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);

  const isLockedPursesVisible = useMemo(
    () => data?.lockedPursesCount !== null && data?.lockedPursesCount !== undefined,
    [data?.lockedPursesCount]
  );

  const isHidden = useMemo(() => {
    if (isSystemLevel) {
      return true;
    }

    if (isLoading) {
      return false;
    }
    return !data?.isShowed;
  }, [data?.isShowed, isLoading, isSystemLevel]);

  const isDetailsLinkVisible = useMemo(() => !isSystemLevel, [isSystemLevel]);

  const tiles = useMemo<DelinquentClaimsStatisticTileConfig[]>(() => {
    const currentTiles: DelinquentClaimsStatisticTileConfig[] = [
      {
        type: DelinquentClaimsStatisticTileType.PaidRequiringDocs,
        testId: 'paid-requiring-docs',
        label: 'Paid, Requiring docs',
        value: data?.paidRequiringDocsCount || 0,
      },
      {
        type: DelinquentClaimsStatisticTileType.RepaymentRequired,
        testId: 'repayment-required',
        label: 'Repayment required',
        value: data?.repaymentRequiredCount || 0,
      },
    ];

    if (data?.lockedPursesCount !== null) {
      currentTiles.push({
        type: DelinquentClaimsStatisticTileType.LockedPurses,
        testId: 'locked-purses',
        label: 'Locked purses',
        value: data?.lockedPursesCount || 0,
      });
    }
    return currentTiles;
  }, [data?.lockedPursesCount, data?.paidRequiringDocsCount, data?.repaymentRequiredCount]);

  return {
    tiles,
    isLockedPursesVisible,
    isDetailsLinkVisible,
    isHidden,
  };
};

export default useDelinquentClaimsStatisticLogic;
